import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { useAuth } from '@/contexts/AuthContext';

export interface ChatProject {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  chat_count?: number;
}

export function useChatProjects() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all projects with chat counts
  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['chat-projects', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      // Fetch projects using db.fetchAll
      const projectsData = await db.fetchAll<ChatProject>('ai_chat_projects', '*', {
        filters: [{ column: 'user_id', operator: 'eq', value: user.id }],
        orderBy: { column: 'updated_at', ascending: false }
      });

      // Fetch chat counts for each project
      const projectsWithCounts = await Promise.all(
        projectsData.map(async (project) => {
          const count = await db.count('ai_chat_sessions', [
            { column: 'project_id', operator: 'eq', value: project.id }
          ]);

          return {
            ...project,
            chat_count: count || 0,
          };
        })
      );

      return projectsWithCounts as ChatProject[];
    },
    enabled: !!user?.id,
  });

  // Create project mutation
  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      const data = await db.insert<ChatProject>('ai_chat_projects', {
        user_id: user.id,
        name,
      });

      if (!data) throw new Error('Failed to create project');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-projects'] });
    },
  });

  // Update project mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const result = await db.update<ChatProject>('ai_chat_projects', 
        { name, updated_at: new Date().toISOString() },
        [{ column: 'id', operator: 'eq', value: id }]
      );

      if (!result || result.length === 0) throw new Error('Failed to update project');
      return result[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-projects'] });
    },
  });

  // Delete project mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await db.delete('ai_chat_projects', [{ column: 'id', operator: 'eq', value: id }]);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chat-projects'] });
    },
  });

  const createProject = useCallback(async (name: string) => {
    return createMutation.mutateAsync(name);
  }, [createMutation]);

  const updateProject = useCallback(async (id: string, name: string) => {
    return updateMutation.mutateAsync({ id, name });
  }, [updateMutation]);

  const deleteProject = useCallback(async (id: string) => {
    return deleteMutation.mutateAsync(id);
  }, [deleteMutation]);

  return {
    projects,
    isLoading,
    createProject,
    updateProject,
    deleteProject,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
